import { useCallback, useEffect, useState } from 'react';

/**
 * Browser-native text-to-speech, packaged as a hook that mirrors the
 * useSpeechRecognition module shape. Built entirely on the Web Speech API
 * (window.speechSynthesis) — no npm packages, API keys, or config required.
 */

const synth = typeof window !== 'undefined' ? window.speechSynthesis : null;

export const isTtsSupported = !!synth;

// Pre-warm the voice list: Chrome/Edge load voices asynchronously, so kicking
// getVoices() at module load beats the latency before the first utterance.
if (synth) {
  synth.getVoices();
}

const normalize = (l) => String(l).replace('_', '-').toLowerCase();

/**
 * Finds the best native voice for a BCP-47 language code. Setting
 * utterance.lang alone is insufficient in Chrome/Edge, so we query getVoices()
 * and assign an actual voice: exact match first, then base-language fallback
 * (e.g. 'hi' for 'hi-IN'). Returns null when nothing matches.
 */
function pickVoice(lang) {
  if (!synth) return null;
  const voices = synth.getVoices();
  const target = normalize(lang);
  let voice = voices.find((v) => normalize(v.lang) === target);
  if (!voice) {
    const base = target.split('-')[0];
    voice = voices.find((v) => normalize(v.lang).startsWith(base));
  }
  return voice || null;
}

/**
 * useTextToSpeech — speaks text aloud via the Web Speech API.
 *
 * @returns {object} api
 * @returns {function} api.speak   (text, lang, onEnd) => void   Speaks `text`
 *   in `lang`, cancelling any ongoing utterance first. `onEnd` fires on both
 *   natural end and error (and immediately when TTS is unsupported), so callers
 *   can chain the next action (e.g. open the mic) regardless of outcome.
 * @returns {function} api.cancel  () => void   Stops any ongoing speech.
 * @returns {boolean}  api.isSpeaking          True while an utterance is active.
 */
export function useTextToSpeech() {
  const [isSpeaking, setIsSpeaking] = useState(false);

  const cancel = useCallback(() => {
    if (!synth) return;
    synth.cancel();
    setIsSpeaking(false);
  }, []);

  const speak = useCallback((text, lang, onEnd) => {
    if (!synth) {
      onEnd?.();
      return;
    }
    synth.cancel(); // never overlap utterances

    const utterance = new SpeechSynthesisUtterance(text);
    utterance.lang = lang;

    const voice = pickVoice(lang);
    if (voice) {
      utterance.voice = voice;
    }

    setIsSpeaking(true);
    utterance.onend = () => {
      setIsSpeaking(false);
      onEnd?.();
    };
    utterance.onerror = (e) => {
      // 'interrupted'/'canceled' just means a newer utterance (or cleanup)
      // called synth.cancel() — expected churn on step/language changes, not a
      // real failure, so ignore it. Surface only genuine errors.
      if (e.error !== 'interrupted' && e.error !== 'canceled') {
        console.error('SpeechSynthesis error:', e);
      }
      setIsSpeaking(false);
      onEnd?.();
    };
    synth.speak(utterance);
  }, []);

  // Stop any in-flight speech when the consumer unmounts.
  useEffect(() => cancel, [cancel]);

  return { speak, cancel, isSpeaking };
}
